import { Box, Typography, LinearProgress, Chip } from '@mui/material';
import DevicesIcon from '@mui/icons-material/Devices';
import AssetsWidget from './AssetsWidget';
import { summary } from '../../data/networkData';
import BugReportIcon from '@mui/icons-material/BugReport';
import ShieldIcon from '@mui/icons-material/Shield';
import VerifiedIcon from '@mui/icons-material/Verified';
import CableIcon from '@mui/icons-material/Cable';
import GppBadIcon from '@mui/icons-material/GppBad';

// ── Compact (top row) components ──────────────────────────────

function CompactCard({ icon, label, value, subvalue, color }) {
  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, marginBottom: 1 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color, fontSize: 36 }}>{icon}</Box>
        <Box>
          <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>{value}</Typography>
          {subvalue && <Typography variant="caption" sx={{ color, fontWeight: 600, display: 'block' }}>{subvalue}</Typography>}
        </Box>
      </Box>
    </Box>
  );
}

function AlertCompact({ label, count, color }) {
  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, marginBottom: 1 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <GppBadIcon sx={{ color, fontSize: 36 }} />
        <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>{count}</Typography>
      </Box>
    </Box>
  );
}

function ISECompact() {
  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, marginBottom: 1 }}>Comply to Connect</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
        <CableIcon sx={{ color: '#00bcd4', fontSize: 36 }} />
        <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>309/342</Typography>
      </Box>
      <LinearProgress variant="determinate" value={90} sx={{ height: 6, borderRadius: 3, backgroundColor: '#1e3a5f', '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' } }} />
    </Box>
  );
}

// ── Expanded (bottom row) components ──────────────────────────

function AlertRow({ severity, message, time }) {
  const color = severity === 'HIGH' ? '#f44336' : severity === 'MEDIUM' ? '#ff9800' : '#4caf50';
  return (
    <Box sx={{ padding: '8px 0', borderBottom: '1px solid #1e3a5f' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0.5 }}>
        <Chip label={severity} size="small" sx={{ backgroundColor: color, color: '#fff', fontWeight: 700, minWidth: 65 }} />
        <Typography variant="caption" sx={{ color: '#7a8fa6' }}>{time}</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: '#ffffff' }}>{message}</Typography>
    </Box>
  );
}

function SIEMExpanded() {
  return (
    <Box sx={{ padding: 1 }}>
      <AlertRow severity="HIGH" message="Brute force detected — jsmith (7 attempts)" time="2 min ago" />
      <AlertRow severity="HIGH" message="Privilege escalation attempt on SRV-DC01" time="11 min ago" />
      <AlertRow severity="MEDIUM" message="User adavis logged in from new location" time="1 hr ago" />
      <AlertRow severity="LOW" message="Account locked — mthompson (3 attempts)" time="2 hr ago" />
    </Box>
  );
}

function FWExpanded() {
  return (
    <Box sx={{ padding: 1 }}>
      <AlertRow severity="HIGH" message="Unauthorized port scan from 10.0.0.44" time="8 min ago" />
      <AlertRow severity="HIGH" message="Outbound connection blocked — DESKTOP-07" time="34 min ago" />
      <AlertRow severity="MEDIUM" message="Weak cipher negotiation detected on WAN" time="1 hr ago" />
      <AlertRow severity="LOW" message="GeoIP block triggered — inbound 185.220.x.x" time="3 hr ago" />
    </Box>
  );
}

function ISEExpanded() {
  return (
    <Box sx={{ padding: 1 }}>
      <Box sx={{ marginBottom: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#7a8fa6' }}>Compliant Devices</Typography>
          <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 700 }}>90%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={90} sx={{ height: 8, borderRadius: 4, backgroundColor: '#1e3a5f', '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' } }} />
      </Box>
      {[['Total Assets', '342', '#ffffff'], ['Compliant', '309', '#4caf50'], ['Remediation VLAN', '33 devices', '#f44336']].map(([label, value, color]) => (
        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
          <Typography variant="body1" sx={{ color: '#7a8fa6', fontWeight: 500 }}>{label}</Typography>
          <Typography variant="body2" sx={{ color, fontWeight: 700 }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function AssetsExpanded() {
  const devices = [
    { hostname: 'SRV-DC01', ip: '10.1.1.10', type: 'Server', status: 'Online' },
    { hostname: 'DESKTOP-07', ip: '10.1.2.45', type: 'Workstation', status: 'Online' },
    { hostname: 'HV-01', ip: '10.1.1.50', type: 'Hypervisor', status: 'Online' },
    { hostname: 'DESKTOP-22', ip: '10.1.2.88', type: 'Workstation', status: 'Offline' },
  ];
  return (
    <Box sx={{ padding: 1 }}>
      {devices.map((d) => (
        <Box key={d.hostname} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #1e3a5f' }}>
          <Box>
            <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>{d.hostname}</Typography>
            <Typography variant="caption" sx={{ color: '#7a8fa6' }}>{d.ip} · {d.type}</Typography>
          </Box>
          <Chip label={d.status} size="small" sx={{ backgroundColor: d.status === 'Online' ? '#4caf50' : '#f44336', color: '#fff', fontWeight: 700 }} />
        </Box>
      ))}
    </Box>
  );
}

function VulnsExpanded() {
  const items = [
    { label: 'Critical', count: 14, color: '#f44336' },
    { label: 'High', count: 31, color: '#ff9800' },
    { label: 'Medium', count: 29, color: '#ffeb3b' },
    { label: 'Low', count: 13, color: '#4caf50' },
  ];
  return (
    <Box sx={{ padding: 1 }}>
      {items.map((item) => (
        <Box key={item.label} sx={{ marginBottom: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#7a8fa6' }}>{item.label}</Typography>
            <Typography variant="body2" sx={{ color: item.color, fontWeight: 700 }}>{item.count}</Typography>
          </Box>
          <LinearProgress variant="determinate" value={(item.count / 87) * 100} sx={{ height: 6, borderRadius: 3, backgroundColor: '#1e3a5f', '& .MuiLinearProgress-bar': { backgroundColor: item.color } }} />
        </Box>
      ))}
    </Box>
  );
}

function RiskGaugeMini({ score }) {
  const color = score >= 85 ? '#4caf50' : score >= 70 ? '#ff9800' : '#f44336';
  const r = 38; const cx = 60; const cy = 60;
  const startAngle = -220; const endAngle = 40;
  const totalArc = endAngle - startAngle;
  const fillArc = (score / 100) * totalArc;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const arc = (angle) => ({ x: cx + r * Math.cos(toRad(angle)), y: cy + r * Math.sin(toRad(angle)) });
  const s = arc(startAngle); const e = arc(startAngle + fillArc); const eEnd = arc(endAngle);
  const largeArc = fillArc > 180 ? 1 : 0;
  const largeArcBg = totalArc > 180 ? 1 : 0;
  return (
    <svg width="120" height="90" viewBox="0 0 120 90">
      <path d={`M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArcBg} 1 ${eEnd.x} ${eEnd.y}`} fill="none" stroke="#1e3a5f" strokeWidth="8" strokeLinecap="round" />
      <path d={`M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 1 ${e.x} ${e.y}`} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" />
      <text x={cx} y={cy + 6} textAnchor="middle" fill={color} fontSize="20" fontWeight="700" fontFamily="monospace">{score}</text>
      <text x={cx} y={cy + 20} textAnchor="middle" fill="#7a8fa6" fontSize="9" fontFamily="sans-serif">/ 100</text>
    </svg>
  );
}

function RiskExpanded() {
  const score = 78;
  const factors = [
    { label: 'STIG Compliance', score: 88, weight: '25%' },
    { label: 'Vuln / Patching', score: 65, weight: '25%' },
    { label: 'C2C Posture', score: 90, weight: '20%' },
    { label: 'Firewall STIGs', score: 82, weight: '15%' },
    { label: 'AV Status', score: 71, weight: '15%' },
  ];
  return (
    <Box sx={{ padding: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1.5 }}>
        <RiskGaugeMini score={score} />
        <Box sx={{ flex: 1 }}>
          {factors.map((f) => (
            <Box key={f.label} sx={{ marginBottom: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 0.25 }}>
                <Typography variant="body2" sx={{ color: '#7a8fa6', fontSize: 11 }}>{f.label}</Typography>
                <Typography variant="body2" sx={{ color: f.score >= 85 ? '#4caf50' : f.score >= 70 ? '#ff9800' : '#f44336', fontWeight: 700, fontSize: 11 }}>{f.score}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={f.score} sx={{ height: 5, borderRadius: 3, backgroundColor: '#1e3a5f', '& .MuiLinearProgress-bar': { backgroundColor: f.score >= 85 ? '#4caf50' : f.score >= 70 ? '#ff9800' : '#f44336' } }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ATOExpanded() {
  return (
    <Box sx={{ padding: 1 }}>
      {[
        ['Status', 'ACTIVE', '#4caf50'],
        ['Type', 'ATO', '#ffffff'],
        ['Authorized', '2025-09-30', '#ffffff'],
        ['Expires', '2026-09-30', '#ff9800'],
        ['Authorizing Official', 'BG Smith', '#ffffff'],
        ['ISSM', 'Jay Thompson', '#ffffff'],
      ].map(([label, value, color]) => (
        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1e3a5f' }}>
          <Typography variant="body1" sx={{ color: '#7a8fa6', fontWeight: 500 }}>{label}</Typography>
          <Typography variant="body2" sx={{ color, fontWeight: 600 }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Widget Registry ────────────────────────────────────────────

export const WIDGET_REGISTRY = {
  assets: {
    id: 'assets',
    compact: <CompactCard icon={<DevicesIcon fontSize="inherit" />} label="Total Assets" value={String(summary.totalDevices)} subvalue={`${summary.online} Online · ${summary.offline} Offline`} color="#00bcd4" />,
    expanded: { title: 'Assets Overview', content: <AssetsWidget /> },
  },
  vulns: {
    id: 'vulns',
    compact: <CompactCard icon={<BugReportIcon fontSize="inherit" />} label="Vulnerability Status" value="87" subvalue="14 Critical" color="#ff9800" />,
    expanded: { title: 'Vulnerabilities', content: <VulnsExpanded /> },
  },
  risk: {
    id: 'risk',
    compact: <CompactCard icon={<ShieldIcon fontSize="inherit" />} label="Risk Posture Score" value="78/100" subvalue="Medium Risk" color="#ff9800" />,
    expanded: { title: 'Risk Posture Breakdown', content: <RiskExpanded /> },
  },
  ato: {
    id: 'ato',
    compact: <CompactCard icon={<VerifiedIcon fontSize="inherit" />} label="ATO Status" value="ACTIVE" subvalue="Expires 2026-09-30" color="#4caf50" />,
    expanded: { title: 'ATO Details', content: <ATOExpanded /> },
  },
  ise: {
    id: 'ise',
    compact: <ISECompact />,
    expanded: { title: 'Comply to Connect Posture', content: <ISEExpanded /> },
  },
  siem: {
    id: 'siem',
    compact: <AlertCompact label="SIEM Alerts" count="4 Open" color="#f44336" />,
    expanded: { title: 'SIEM Alerts', content: <SIEMExpanded /> },
  },
  fw: {
    id: 'fw',
    compact: <AlertCompact label="Firewall Alerts" count="4 Open" color="#f44336" />,
    expanded: { title: 'Firewall Alerts', content: <FWExpanded /> },
  },
};
